import { IonModal, IonContent, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonList, IonItem, IonLabel, IonSelect, IonSelectOption, IonIcon, IonRange } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { close } from 'ionicons/icons';
import { SearchFilters } from '../types/post';
import { industryAndServiceKeys, activityTypeKeys } from '../types/user';

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: SearchFilters) => void;
  currentFilters: SearchFilters;
}

export const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  onApply,
  currentFilters
}) => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Delay rendering of Ionic components until after initial mount
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const { t } = useTranslation();
  const [filters, setFilters] = useState<SearchFilters>(currentFilters);

  useEffect(() => {
    setFilters(currentFilters);
  }, [currentFilters]);

  const handleDistanceChange = (value: string) => {
    // Clear conflicting filters
    if (value === 'other_countries') {
      setFilters(prev => ({
        ...prev,
        proximity: value,
        sameCountry: undefined
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        proximity: value as SearchFilters['proximity']
      }));
    }
  };

  const handleKmChange = (value: number) => {
    setFilters(prev => ({
      ...prev,
      kmRange: value.toString()
    }));
  };

  const handleActivityChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      activityType: value
    }));
  };

  const handleSortOrderChange = (value: 'recent_first' | 'oldest_first') => {
    setFilters(prev => ({
      ...prev,
      sortOrder: value
    }));
  };

  const clearFilters = () => {
    setFilters({});
  };

  const applyFilters = () => {
    onApply(filters);
    onClose();
  };

  // Combine and deduplicate activity keys
  const uniqueActivityKeys = Array.from(new Set([...industryAndServiceKeys]));

  return (
    <IonModal 
      isOpen={isOpen && isReady} 
      onDidDismiss={onClose}
      style={{
        '--transition-duration': '0.2s'
      }}
    >
      <IonHeader className="ion-no-border">
        <IonToolbar className="glass-effect" style={{ 
          '--background': 'transparent',
          '--border-width': '0',
          '--padding-top': '10px',
          '--padding-bottom': '10px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 16px'
          }}>
            <h2 style={{
              margin: 0,
              color: '#0068B9',
              fontWeight: 700,
              fontSize: '1.5rem'
            }}>
              {t('filters.title')}
            </h2>
            <div style={{
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              background: 'rgba(0, 104, 185, 0.1)',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
            }}
            onClick={onClose}
            >
              <IonIcon 
                icon={close} 
                style={{ 
                  fontSize: '20px', 
                  color: '#0068B9'
                }} 
              />
            </div>
          </div>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonList style={{
          background: 'transparent',
          padding: '10px 0'
        }}>
          <IonItem style={{
            '--background': 'rgba(255, 255, 255, 0.7)',
            '--border-radius': '16px',
            margin: '8px 16px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
          }}>
            <IonLabel style={{ fontWeight: 500, color: '#1f2937' }}>{t('filters.distance')}</IonLabel>
            <IonSelect
              value={filters.proximity}
              onIonChange={e => handleDistanceChange(e.detail.value)}
              interface="popover"
            >
              <IonSelectOption value="same_city">{t('filters.same_city')}</IonSelectOption>
              <IonSelectOption value="other_cities">{t('filters.other_cities')}</IonSelectOption>
              <IonSelectOption value="other_countries">{t('filters.other_countries')}</IonSelectOption>
            </IonSelect>
          </IonItem>

          {filters.proximity === 'other_cities' && isReady && (
            <div className="distance-range-container" style={{ 
              padding: '20px 24px',
              margin: '8px 16px 16px',
              borderRadius: '20px',
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 20px rgba(0, 0, 0, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
              }}>
                <IonLabel style={{ 
                  color: '#0068B9',
                  fontSize: '16px',
                  fontWeight: '600'
                }}>
                  {t('filters.km_range')}
                </IonLabel>
                <div style={{
                  background: 'linear-gradient(135deg, #0068B9 0%, #29C6F3 100%)',
                  color: 'white',
                  padding: '6px 14px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  boxShadow: '0 4px 10px rgba(0, 104, 185, 0.2)'
                }}>
                  {filters.kmRange || '0'} km
                </div>
              </div>
              
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <IonRange
                  min={0}
                  max={9000}
                  step={300}
                  value={parseInt(filters.kmRange || '0')}
                  onIonChange={e => handleKmChange(e.detail.value as number)}
                  style={{
                    '--bar-height': '6px',
                    '--bar-border-radius': '3px',
                    '--knob-size': '20px',
                    '--bar-background': 'rgba(var(--ion-color-medium-rgb), 0.15)',
                    '--bar-background-active': 'var(--ion-color-primary)',
                    '--knob-background': 'white',
                    '--knob-box-shadow': '0 2px 8px rgba(var(--ion-color-primary-rgb), 0.3)',
                    '--knob-border': '2px solid var(--ion-color-primary)',
                    'padding': '0',
                    'margin': '0'
                  }}
                />
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  color: 'var(--ion-color-medium)',
                  fontSize: '13px',
                  paddingTop: '4px'
                }}>
                  <span>0 km</span>
                  <span>9000 km</span>
                </div>
              </div>
            </div>
          )}

          <IonItem style={{
            '--background': 'rgba(255, 255, 255, 0.7)',
            '--border-radius': '16px',
            margin: '8px 16px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
          }}>
            <IonLabel style={{ fontWeight: 500, color: '#1f2937' }}>{t('filters.activity_type')}</IonLabel>
            <IonSelect
              value={filters.activityType}
              onIonChange={e => handleActivityChange(e.detail.value)}
              interface="popover"
            >
              {uniqueActivityKeys.map(activity => (
                <IonSelectOption key={activity} value={activity}>
                  {t(`registration.industries.${activity}`)}
                </IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>

          <IonItem style={{
            '--background': 'rgba(255, 255, 255, 0.7)',
            '--border-radius': '16px',
            margin: '8px 16px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
          }}>
            <IonLabel style={{ fontWeight: 500, color: '#1f2937' }}>{t('filters.sort_order')}</IonLabel>
            <IonSelect
              value={filters.sortOrder}
              onIonChange={e => handleSortOrderChange(e.detail.value)}
              interface="popover"
            >
              <IonSelectOption value="recent_first">{t('filters.recent_first')}</IonSelectOption>
              <IonSelectOption value="oldest_first">{t('filters.oldest_first')}</IonSelectOption>
            </IonSelect>
          </IonItem>
        </IonList>

        <div style={{ 
          padding: '16px', 
          display: 'flex', 
          gap: '12px', 
          justifyContent: 'center',
          marginTop: '16px'
        }}>
          <IonButton 
            fill="outline" 
            onClick={clearFilters}
            color="medium"
            style={{
              '--border-radius': '16px',
              '--border-width': '2px',
              '--padding-start': '24px',
              '--padding-end': '24px',
              fontWeight: '600'
            }}
          >
            {t('filters.clear')}
          </IonButton>
          <IonButton 
            onClick={applyFilters}
            style={{
              '--background': 'linear-gradient(135deg, #0068B9 0%, #29C6F3 100%)',
              '--border-radius': '16px',
              '--padding-start': '24px',
              '--padding-end': '24px',
              '--box-shadow': '0 8px 16px rgba(0, 104, 185, 0.2)',
              fontWeight: '600'
            }}
          >
            {t('filters.apply')}
          </IonButton>
        </div>
      </IonContent>
    </IonModal>
  );
};
